import React, { useState, useRef, useCallback, useContext } from 'react';
import ReactDOM from 'react-dom';
import { Box, Typography, Tooltip, IconButton, Divider } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DeleteOutlined, DragIndicatorOutlined } from '@mui/icons-material';
import { PRIMARY } from '../constants';
import { CtxMenuCtx, PreviewCtx, DragCtx } from '../contexts';
import { useDragEndReset } from '../hooks';
import { AnyElement } from './elements';

// ─── ContextMenu ──────────────────────────────────────────────────────────────
export function ContextMenu({ menu, onDuplicate, onDelete, onCopy, onPaste, clipboard, onSectionDuplicate, onSectionCopy, onSectionPaste, onSectionDelete, sectionClipboard, onClose }) {
  React.useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    window.addEventListener('mousedown', onClose);
    return () => { window.removeEventListener('keydown', handler); window.removeEventListener('mousedown', onClose); };
  }, [onClose]);

  if (!menu) return null;

  const MENU_W = 170;
  const x = Math.min(menu.x, window.innerWidth  - MENU_W - 8);
  const y = Math.min(menu.y, window.innerHeight - 160);

  const isSection = typeof menu.elId === 'string' && menu.elId.startsWith('section:');
  const sectionId = isSection ? menu.elId.replace('section:', '') : null;

  const items = isSection ? [
    { label: 'Duplicate', icon: '⧉', action: () => { onSectionDuplicate(sectionId); onClose(); } },
    { label: 'Copy',      icon: '⎘', action: () => { onSectionCopy(sectionId);      onClose(); } },
    { label: 'Paste',     icon: '⎗', action: () => { onSectionPaste(sectionId);     onClose(); }, disabled: !sectionClipboard },
    { divider: true },
    { label: 'Delete',    icon: '🗑', action: () => { onSectionDelete(sectionId);   onClose(); }, danger: true },
  ] : [
    { label: 'Duplicate', icon: '⧉', action: () => { onDuplicate(menu.elId); onClose(); } },
    { label: 'Copy',      icon: '⎘', action: () => { onCopy(menu.elId);      onClose(); } },
    { label: 'Paste',     icon: '⎗', action: () => { onPaste(menu.elId);     onClose(); }, disabled: !clipboard },
    { divider: true },
    { label: 'Delete',    icon: '🗑', action: () => { onDelete(menu.elId);    onClose(); }, danger: true },
  ];

  return ReactDOM.createPortal(
    <Box onMouseDown={e => e.stopPropagation()}
      sx={{ position: 'fixed', top: y, left: x, width: MENU_W, zIndex: 9999, background: '#fff', borderRadius: '10px', boxShadow: '0 8px 32px rgba(0,0,0,0.18), 0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #e8edf2', overflow: 'hidden', py: 0.5 }}>
      {items.map((item, i) => item.divider ? (
        <Divider key={i} sx={{ borderColor: '#f0f2f5', my: 0.5 }} />
      ) : (
        <Box key={item.label} onClick={item.disabled ? undefined : item.action}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.75, py: 1, cursor: item.disabled ? 'default' : 'pointer', fontSize: '0.82rem', fontWeight: 500, color: item.disabled ? '#c0c8d4' : item.danger ? '#d32f2f' : '#1a1a2e', transition: 'background 0.1s', '&:hover': { background: item.disabled ? 'transparent' : item.danger ? alpha('#d32f2f', 0.06) : '#f5f7fa' } }}>
          <Box component="span" sx={{ fontSize: '0.95rem', lineHeight: 1 }}>{item.icon}</Box>
          {item.label}
        </Box>
      ))}
    </Box>,
    document.body,
  );
}

// ─── Section ──────────────────────────────────────────────────────────────────
export function Section({
  section, sectionIdx, sectionsCount,
  selectedId, selectedSectionId, onSelectSection, onSelect, onChange,
  onDelete, onDrop, onSelectChild, onReorder, onSectionReorder,
  onSectionDuplicate, onSectionCopy, onSectionPaste, sectionClipboard,
  onUpdateEl, onDeleteEl, onMoveChildToSection, onMoveChildToContainer,
  onInsertNewChildAt, onMoveTopLevelIntoContainer, onRename, onMergeAsRow, onSplitDropIntoEmpty,
  stickyHeaderHeight = 0,
}) {
  const [dropIntent, setDropIntent] = useState(null);
  const [editingLabel, setEditingLabel] = useState(false);
  const [labelDraft, setLabelDraft] = useState(section.label);
  const [sectionDropPos, setSectionDropPos] = useState(null);
  const dragIdxRef  = useRef(null);
  const sectionRef  = useRef();
  const labelRef    = useRef();
  const ctx         = useContext(DragCtx);
  const drag        = ctx.drag;
  const sectionDrag = ctx.sectionDrag;
  const bp          = ctx.breakpointRef?.current || 'desktop';
  const isPreview   = useContext(PreviewCtx);
  const { open: openMenu } = useContext(CtxMenuCtx);
  const sectionVisible = (section.visibleOn || {})[bp] !== false;

  useDragEndReset(useCallback(() => { setDropIntent(null); setSectionDropPos(null); }, []));

  const getDropIntent = useCallback((clientX, clientY) => {
    if (!sectionRef.current) return { pos: section.elements.length, side: 'after' };
    const rows = Array.from(sectionRef.current.querySelectorAll(':scope > [data-el-row]'));
    if (!rows.length) return { pos: 0, side: 'before' };
    let insertPos = rows.length;
    for (let i = 0; i < rows.length; i++) {
      const rect = rows[i].getBoundingClientRect();
      if (clientY < rect.top + rect.height / 2) { insertPos = i; break; }
    }
    const nearestIdx = Math.min(insertPos, rows.length - 1);
    const nearestRect = rows[nearestIdx].getBoundingClientRect();
    const isInsideRow = clientY >= nearestRect.top && clientY < nearestRect.bottom;
    if (isInsideRow) {
      const relX = clientX - nearestRect.left;
      if (relX > nearestRect.width * 0.65 && nearestRect.width > 80) return { pos: nearestIdx, side: 'right' };
    }
    return { pos: insertPos, side: insertPos === rows.length ? 'after' : 'before' };
  }, [section.elements.length]);

  const handleSectionDragOver = (e) => {
    const d = drag.current;
    if (!d) return;
    e.preventDefault();
    setDropIntent(getDropIntent(e.clientX, e.clientY));
  };

  const _doPlainDrop = (d, pos, e) => {
    if (d.kind === 'topLevel') {
      if (d.fromSectionId === section.id) {
        const from = dragIdxRef.current;
        if (from !== null && from !== pos - 1 && from !== pos) onReorder(section.id, from, pos > from ? pos - 1 : pos);
      } else {
        onMoveChildToSection(d.elId, section.id, pos);
      }
    } else if (d.kind === 'palette') {
      onDrop(e, section.id, pos);
    } else if (d.kind === 'child' || d.kind === 'layer') {
      onMoveChildToSection(d.elId, section.id, pos);
    }
  };

  const handleSectionDrop = (e) => {
    e.preventDefault();
    const intent = getDropIntent(e.clientX, e.clientY);
    const d = drag.current;
    if (!d) return;
    if (intent.side === 'right') {
      const targetEl = section.elements[intent.pos];
      if (targetEl && d.kind === 'topLevel' && d.elId !== targetEl.id) {
        onMergeAsRow(section.id, targetEl.id, d.elId, intent.pos);
      } else if (targetEl && d.kind === 'palette') {
        onMergeAsRow(section.id, targetEl.id, null, intent.pos, d.elementType);
      } else {
        _doPlainDrop(d, intent.pos + 1, e);
      }
    } else {
      _doPlainDrop(d, intent.side === 'after' ? intent.pos + 1 : intent.pos, e);
    }
    drag.current = null;
    dragIdxRef.current = null;
    ctx.resetAll();
    setDropIntent(null);
  };

  const HDropLine = ({ show }) => show ? (
    <Box sx={{ height: 2, background: PRIMARY, borderRadius: 1, my: 0.25, boxShadow: `0 0 6px ${alpha(PRIMARY, 0.5)}`, position: 'relative', zIndex: 10 }}>
      <Box sx={{ position: 'absolute', left: -4, top: -3, width: 8, height: 8, borderRadius: '50%', background: PRIMARY }} />
    </Box>
  ) : null;

  const VDropLine = ({ show }) => show ? (
    <Box sx={{ position: 'absolute', top: 0, right: -1, bottom: 0, width: 2, background: PRIMARY, borderRadius: 1, zIndex: 20, boxShadow: `0 0 6px ${alpha(PRIMARY, 0.6)}`, pointerEvents: 'none' }}>
      <Box sx={{ position: 'absolute', top: -4, left: -3, width: 8, height: 8, borderRadius: '50%', background: PRIMARY }} />
      <Box sx={{ position: 'absolute', bottom: -4, left: -3, width: 8, height: 8, borderRadius: '50%', background: PRIMARY }} />
    </Box>
  ) : null;

  const handleSectionWrapperDragOver = (e) => {
    if (sectionDrag.current === null) return;
    e.preventDefault(); e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setSectionDropPos(e.clientY < rect.top + rect.height / 2 ? 'before' : 'after');
  };

  const handleSectionWrapperDrop = (e) => {
    if (sectionDrag.current === null) return;
    e.preventDefault(); e.stopPropagation();
    const fromIdx = sectionDrag.current.fromIdx;
    const toIdx   = sectionDropPos === 'before' ? sectionIdx : sectionIdx + 1;
    onSectionReorder(fromIdx, toIdx > fromIdx ? toIdx - 1 : toIdx);
    sectionDrag.current = null;
    setSectionDropPos(null);
  };

  if (isPreview && !sectionVisible) return null;

  return (
    <Box
      onDragOver={handleSectionWrapperDragOver}
      onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setSectionDropPos(null); }}
      onDrop={handleSectionWrapperDrop}
      sx={{ position: 'relative' }}
    >
      {!isPreview && sectionDropPos === 'before' && (
        <Box sx={{ height: 3, background: PRIMARY, borderRadius: 2, mx: 2, mb: 0.5, boxShadow: `0 0 8px ${alpha(PRIMARY, 0.5)}` }} />
      )}

      <Box
        onDragOver={handleSectionDragOver}
        onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDropIntent(null); }}
        onDrop={handleSectionDrop}
        sx={{ position: 'relative' }}
        onClick={() => onSelect(null)}
      >
        {!isPreview && (
          <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5, zIndex: 10, opacity: 0, transition: 'opacity 0.15s', '.swrap:hover &': { opacity: 1 } }}>
            <Tooltip title="Delete section">
              <IconButton size="small" onClick={e => { e.stopPropagation(); onDelete(section.id); }} sx={{ background: '#fff', border: '1px solid #e8edf2', width: 28, height: 28, '&:hover': { background: alpha('#d32f2f', 0.08), color: '#d32f2f' } }}>
                <DeleteOutlined sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        <Box className="swrap" style={section.style}
          id={`section-${section.id}`}
          data-section-id={section.id}
          onClick={e => { e.stopPropagation(); onSelectSection(section.id); }}
          sx={{
            ...(!isPreview && selectedSectionId === section.id ? { outline: `2px solid ${alpha(PRIMARY, 0.6)}`, outlineOffset: '0px' } : {}),
            ...(!isPreview && !sectionVisible ? { opacity: 0.35 } : {}),
            position: 'relative',
          }}>
          {stickyHeaderHeight > 0 && <Box sx={{ height: stickyHeaderHeight, flexShrink: 0, pointerEvents: 'none' }} />}
          {!isPreview && !sectionVisible && (
            <Box sx={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', background: '#e67e22', color: '#fff', fontSize: '0.65rem', fontWeight: 700, px: 1, py: 0.3, borderRadius: 1, zIndex: 20, letterSpacing: '0.05em', pointerEvents: 'none' }}>
              HIDDEN ON {bp.toUpperCase()}
            </Box>
          )}
          <Box sx={section.layout === 'boxed' ? { maxWidth: section.maxWidth || '1200px', mx: 'auto', width: '100%' } : {}}>
            {section.elements.length === 0 && !isPreview ? (
              <Box sx={{ textAlign: 'center', minHeight: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, color: alpha(PRIMARY, 0.35), border: `2px dashed ${alpha(PRIMARY, 0.2)}`, borderRadius: 2, background: dropIntent ? alpha(PRIMARY, 0.04) : 'transparent', transition: 'background 0.1s' }}>
                <Typography sx={{ fontWeight: 600 }}>Drop elements here</Typography>
                <Typography variant="caption">Drag from the left panel</Typography>
              </Box>
            ) : (
              <Box ref={sectionRef} sx={{ display: 'flex', flexDirection: 'column' }}>
                {!isPreview && <HDropLine show={dropIntent?.side !== 'right' && dropIntent?.pos === 0} />}
                {section.elements.map((el, idx) => (
                  <React.Fragment key={el.id}>
                    <Box data-el-row={idx} sx={{ position: 'relative', overflow: 'visible' }}>
                      <Box sx={{ position: 'relative', overflow: 'visible' }}>
                        <AnyElement
                          el={el} selected={selectedId === el.id}
                          onSelect={() => onSelect(el.id)}
                          onChange={updated => onChange(section.id, updated)}
                          isDragOver={false}
                          onDragStart={e => {
                            e.stopPropagation();
                            dragIdxRef.current = idx;
                            drag.current = { kind: 'topLevel', elId: el.id, fromSectionId: section.id, fromIdx: idx };
                          }}
                          selectedId={selectedId}
                          onSelectChild={onSelectChild}
                          onUpdateEl={onUpdateEl}
                          onDeleteEl={onDeleteEl}
                          onMoveChildToSection={onMoveChildToSection}
                          onMoveChildToContainer={onMoveChildToContainer}
                          onInsertNewChildAt={onInsertNewChildAt}
                          onMoveTopLevelIntoContainer={onMoveTopLevelIntoContainer}
                          onSplitDropIntoEmpty={onSplitDropIntoEmpty}
                          sectionId={section.id}
                        />
                        {!isPreview && <VDropLine show={dropIntent?.side === 'right' && dropIntent?.pos === idx} />}
                      </Box>
                    </Box>
                    {!isPreview && <HDropLine show={dropIntent?.side !== 'right' && dropIntent?.pos === idx + 1} />}
                  </React.Fragment>
                ))}
              </Box>
            )}
          </Box>
        </Box>

        {!isPreview && (
          <Box ref={labelRef} draggable
            onDragStart={e => {
              e.stopPropagation();
              sectionDrag.current = { fromIdx: sectionIdx };
              const node = e.currentTarget;
              const rect = node.getBoundingClientRect();
              const clone = node.cloneNode(true);
              Object.assign(clone.style, { position: 'fixed', left: '-9999px', top: '0', opacity: '0.9', background: '#fff', borderRadius: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' });
              document.body.appendChild(clone);
              e.dataTransfer.setDragImage(clone, e.clientX - rect.left, e.clientY - rect.top);
              setTimeout(() => document.body.contains(clone) && document.body.removeChild(clone), 200);
            }}
            onDragEnd={() => { sectionDrag.current = null; setSectionDropPos(null); }}
            onContextMenu={e => { e.preventDefault(); e.stopPropagation(); openMenu(`section:${section.id}`, e.clientX, e.clientY); }}
            sx={{ position: 'absolute', top: 0, left: 0, display: 'flex', alignItems: 'center', gap: 0.75, background: alpha(PRIMARY, 0.08), px: 1, py: 0.25, borderRadius: '0 0 4px 0', cursor: 'grab', userSelect: 'none', '&:active': { cursor: 'grabbing' } }}>
            <DragIndicatorOutlined sx={{ fontSize: 11, color: alpha(PRIMARY, 0.5) }} />
            {editingLabel ? (
              <input autoFocus value={labelDraft} onChange={e => setLabelDraft(e.target.value)}
                onBlur={() => { setEditingLabel(false); onRename(section.id, labelDraft || 'Section'); }}
                onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setEditingLabel(false); setLabelDraft(section.label); } e.stopPropagation(); }}
                onClick={e => e.stopPropagation()}
                style={{ fontSize: '0.65rem', fontWeight: 700, color: PRIMARY, background: 'transparent', border: 'none', outline: 'none', fontFamily: 'inherit', letterSpacing: '0.05em', textTransform: 'uppercase', width: Math.max(60, labelDraft.length * 8) + 'px' }} />
            ) : (
              <Typography onDoubleClick={e => { e.stopPropagation(); setEditingLabel(true); setLabelDraft(section.label); }}
                sx={{ fontSize: '0.65rem', fontWeight: 700, color: PRIMARY, letterSpacing: '0.05em', textTransform: 'uppercase', cursor: 'grab', userSelect: 'none' }}>
                {section.label}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {!isPreview && sectionDropPos === 'after' && (
        <Box sx={{ height: 3, background: PRIMARY, borderRadius: 2, mx: 2, mt: 0.5, boxShadow: `0 0 8px ${alpha(PRIMARY, 0.5)}` }} />
      )}
    </Box>
  );
}